/** Classic Worker: Pyodide в отдельном потоке — бесконечный цикл не вешает вкладку. */
export const PYODIDE_WORKER_SOURCE = `
"use strict";
const PYODIDE_INDEX = "https://cdn.jsdelivr.net/pyodide/v0.26.4/full/";
const STDOUT_MAX = 8000;

let pyodide = null;
let loading = null;
const loadedPackages = new Set();
let stdoutLen = 0;
let stdoutTruncated = false;
let inputWaiter = null;

function postChunk(type, chunk) {
  if (stdoutTruncated || stdoutLen >= STDOUT_MAX) {
    stdoutTruncated = true;
    return;
  }
  const part = String(chunk);
  const room = STDOUT_MAX - stdoutLen;
  const slice = part.length > room ? part.slice(0, room) : part;
  stdoutLen += slice.length;
  self.postMessage({ type: type, chunk: slice });
  if (stdoutLen >= STDOUT_MAX) {
    stdoutTruncated = true;
  }
}

function formatRunError(e) {
  const raw = String(e && e.message ? e.message : e);
  if (stdoutTruncated) {
    return "Превышен лимит вывода (" + STDOUT_MAX + " символов). Выполнение остановлено.";
  }
  if (/KeyboardInterrupt/i.test(raw)) {
    return "Выполнение остановлено (прервано по таймауту).";
  }
  if (/Errno 29|I\\/O error/i.test(raw)) {
    return "Вывод в консоль прерван (лимит или таймаут). Доска не зависла.";
  }
  return raw;
}

function postPhase(phase, detail) {
  self.postMessage({ type: "phase", phase: phase, detail: detail || "" });
}

async function ensurePyodide(interruptBuffer) {
  if (pyodide) {
    if (interruptBuffer && pyodide.setInterruptBuffer) {
      pyodide.setInterruptBuffer(interruptBuffer);
    }
    return pyodide;
  }
  if (!loading) {
    loading = (async function () {
      try {
        postPhase("loading_runtime", "Загрузка Python…");
        importScripts(PYODIDE_INDEX + "pyodide.js");
        const py = await loadPyodide({ indexURL: PYODIDE_INDEX });
        // write, а не batched: batched отдаёт строку уже без перевода строки,
        // и вывод склеивался в одну строку (таблицы из print превращались в кашу).
        // write получает байты как есть; TextDecoder со stream не рвёт кириллицу
        // на границе кусков.
        const outDecoder = new TextDecoder();
        const errDecoder = new TextDecoder();
        py.setStdout({
          write: function (buf) {
            postChunk("stdout", outDecoder.decode(buf, { stream: true }));
            return buf.length;
          },
          // Как терминал: построчная буферизация, print до input() виден сразу.
          isatty: true,
        });
        py.setStderr({
          write: function (buf) {
            postChunk("stderr", errDecoder.decode(buf, { stream: true }));
            return buf.length;
          },
          // Как терминал: построчная буферизация, print до input() виден сразу.
          isatty: true,
        });

        // Важно: в JS globalThis, не в py.globals — иначе "from js import" падает.
        self._wb_request_input = function (prompt) {
          return new Promise(function (resolve) {
            inputWaiter = resolve;
            self.postMessage({ type: "inputRequest", prompt: String(prompt || "") });
          });
        };

        try {
          await py.runPythonAsync(
            "from pyodide.ffi import run_sync\\n" +
            "from js import _wb_request_input\\n" +
            "def input(prompt=\\"\\"):\\n" +
            "    v = run_sync(_wb_request_input(str(prompt) if prompt is not None else \\"\\"))\\n" +
            "    return \\"\\" if v is None else str(v)\\n"
          );
        } catch (inputErr) {
          // Без SharedArrayBuffer run_sync может быть недоступен — не ломаем весь runtime.
          await py.runPythonAsync(
            "def input(prompt=\\"\\"):\\n" +
            "    raise RuntimeError(\\"input() сейчас недоступен в этой среде\\")\\n"
          );
        }

        await py.runPythonAsync(
          "import math, bisect, sys, collections, functools\\n"
        );

        pyodide = py;
        return py;
      } catch (e) {
        loading = null;
        pyodide = null;
        throw e;
      }
    })();
  }
  const py = await loading;
  if (interruptBuffer && py.setInterruptBuffer) {
    py.setInterruptBuffer(interruptBuffer);
  }
  return py;
}

self.onmessage = async function (ev) {
  const msg = ev.data || {};
  if (msg.type === "inputResponse") {
    if (inputWaiter) {
      var fn = inputWaiter;
      inputWaiter = null;
      fn(msg.value);
    }
    return;
  }
  if (msg.type !== "run") return;

  stdoutLen = 0;
  stdoutTruncated = false;
  try {
    const py = await ensurePyodide(msg.interruptBuffer || null);
    const needed = (msg.packages || []).filter(function (p) {
      return !loadedPackages.has(p);
    });
    if (needed.length) {
      postPhase("loading_packages", needed.join(", "));
      await py.loadPackage(needed);
      needed.forEach(function (p) { loadedPackages.add(p); });
    }
    if (msg.interruptBuffer && msg.interruptBuffer[0]) {
      msg.interruptBuffer[0] = 0;
    }
    postPhase("running", "Выполнение…");
    await py.runPythonAsync(msg.code);
    if (stdoutTruncated) {
      self.postMessage({
        type: "done",
        ok: false,
        error: "Превышен лимит вывода (" + STDOUT_MAX + " символов).",
        stdoutTruncated: true,
      });
      return;
    }
    self.postMessage({ type: "done", ok: true });
  } catch (e) {
    self.postMessage({
      type: "done",
      ok: false,
      error: formatRunError(e),
      stdoutTruncated: stdoutTruncated,
    });
  }
};
`;
